import { Component, OnInit, OnDestroy } from '@angular/core';

import { TreeNode } from 'primeng/api';
import { BehaviorSubject, Subscription } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { BriefingsService } from '../../services/briefings.service';
import { BriefingListItemModel } from '../../../../Common/MonitorShared/model/briefing-list-item.model';
import { BriefingTemplateService } from '../../../../Common/MonitorShared/services/briefing-template.service';
import { BriefingModel } from '../../models/briefing.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import {
    BriefingSaveTemplateOption,
    briefingTemplateContentNodes,
    TemplateOptionEnum,
    briefingBlockTypeToTemplateContentOptionMap
} from './briefingSaveTemplateOptions/briefing-save-template-option.const';
import { BriefingBlockTypeEnum } from '../../enums/briefing-block-type.enum';
import { BriefingGeneralStylesModel } from '../../models/briefing-general-styles.model';
import { MonitoringImageStyleModel } from '../../../Shared/models/monitoring-image-style.model';
import { MonitoringTextStyleModel } from '../../../Shared/models/monitoring-text-style.model';
import { MonitoringDateStyleModel } from '../../../Shared/models/monitoring-date-style-model';
import { MentionCustomizationModel } from '../../../Shared/models/mention-customization.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CuratedMentionListDisplayElementModel } from '../../models/curated-mention-list-display-element.model';
import { MentionSearchModel } from '../../../Shared/models/mention-search.model';
import { BriefingSendAtModel } from '../../models/briefing-send-at.model';
import { ManageTemplateDialogComponent } from '../../../../Common/MonitorShared/components/ManageTemplateDialog/manage-template-dialog.component';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { MonitoringLayoutStyle } from '@Monitor/Shared/models/monitoring-layout-style.model';
import { BriefingDeliveryMethodEnum } from '../../enums/briefing-delivery-method.enum';
import { LayoutBlockDisplayRatiosEnum } from '../../../../Common/PageBuilder/enums/layout-block-display-ratios.enum';
import { BlockModel } from '../../../../Common/PageBuilder/models/block.model';
import { LayoutBlock } from '@Common/PageBuilder/models/layout-block.model';
import { CuratorStyle } from '@Common/PageBuilder/models/curator-style.model';

enum SaveTemplateState {
    NEW,
    UPDATE
}

/**
 * Dialog provides options to save the currentBriefing as either
 * - save as new template
 * - update an existing template
 */
@Component({
    selector: 'agpr-briefing-save-template-dialog',
    templateUrl: './briefing-save-template-dialog.component.html',
    styleUrls: ['./briefing-save-template-dialog.component.scss'],
    standalone: false
})
export class BriefingSaveTemplateDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    briefingTemplateListItems: DropdownOptionModel<BriefingListItemModel>[] = [];
    existingBriefingTemplateDropdown: DropdownOptionModel<BriefingListItemModel> = null;
    newBriefingTemplateName: string = '';

    saveType: SaveTemplateState = SaveTemplateState.NEW;
    formValid: boolean = false;
    briefingSaveTemplateOption: BriefingSaveTemplateOption = {};

    // tree structured treeNode array containing all possible selections
    templateOptions: TreeNode<string>[] = [];

    // flat structured treeNode array containing individual treeNode selections
    selectedOptions: TreeNode<string>[] = [];

    readonly id = 'briefingSaveTemplateDialog';
    readonly saveTemplateState = SaveTemplateState;
    readonly buttonTypes = ButtonTypesEnum;

    private subscriptions: Subscription[] = [];
    private currentBriefing: BriefingModel;

    constructor(
        public coreDialogService: CoreDialogService,
        private briefingsService: BriefingsService,
        private briefingTemplateService: BriefingTemplateService,
        private localizationService: LocalizationService
    ) {
        super(coreDialogService, 'BriefingSaveTemplate');
    }

    ngOnInit() {
        this.subscriptions.push(
            this.briefingTemplateService.all$.subscribe(templates => {
                this.briefingTemplateListItems = templates
                    .filter(item => item.deliveryMethod !== BriefingDeliveryMethodEnum.ONLINE)
                    .map(x => new DropdownOptionModel(x.name, x)) || [];
            }),
            this.briefingsService.getFocusedResource().subscribe( currentBriefing => {
                this.currentBriefing = currentBriefing;
                this.templateOptions = this.templateOptionToTreeNode(briefingTemplateContentNodes);
                this.contentBlocksToTreeNode();
                this.handleOptionChange();
            })
        );

        this.validate();
    }

    validate() {
        switch (this.saveType) {
            case SaveTemplateState.NEW:
                this.formValid = this.newBriefingTemplateName.trim().length > 0;
                break;

            case SaveTemplateState.UPDATE:
                this.formValid = !!this.existingBriefingTemplateDropdown?.value;
                break;

            default:
                this.formValid = false;
                break;
        }
    }


    create() {
        const template = this.processTemplateContent();
        template.name = this.newBriefingTemplateName?.trim();
        delete template.id;

        this.briefingTemplateService.createFromBriefing(template).subscribe(() => {
            this.closeDialog();
        });
    }

    update() {
        const template = this.processTemplateContent();
        template.name = this.existingBriefingTemplateDropdown.value.name;
        template.id = this.existingBriefingTemplateDropdown.value.id;

        this.briefingTemplateService.updateFromBriefing(template).subscribe(() => {
            this.closeDialog();
        });
    }


    openManageTemplatesDialog() {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageTemplateDialogComponent, io);
    }

    /**
     * Updates the current value
     */
    handleOptionChange() {
        Object.keys(this.briefingSaveTemplateOption).forEach(key => {
            this.briefingSaveTemplateOption[key] = false;
        });
        this.selectedOptions.forEach(item => this.briefingSaveTemplateOption[item.data] = true);
    }

    cancel() {
        this.closeDialog();
    }

    private processTemplateContent(): BriefingModel {
        const clone = this.currentBriefing.clone();
        clone.blocks = this.currentBriefing.blocks?.map(block => block.clone());

        // Styles
        const copyStyles = this.briefingSaveTemplateOption[TemplateOptionEnum.STYLES];
        if (!copyStyles) {
            clone.generalStyles = new BriefingGeneralStylesModel();
        }

        // Content blocks
        clone.blocks = clone.blocks.filter((block, index) => {
            if (block.isLayoutBlockType()) {
                const layoutVisible = this.cleanUpBlock(block, copyStyles, '' + index);
                if (layoutVisible) {
                    (block as LayoutBlock).blocks.forEach((column, cIndex) => {
                        column.forEach((row, rIndex) => {
                            this.cleanUpBlock(row, copyStyles, '' + index + cIndex + rIndex);
                        });
                    });
                }
                return layoutVisible;
            } else {
                return this.cleanUpBlock(block, copyStyles, '' + index);
            }
        });

        // Delivery/Scheduling Options
        if (!this.briefingSaveTemplateOption[TemplateOptionEnum.SUBJECT_LINE]) {
            clone.subject = '';
        }

        if (!this.briefingSaveTemplateOption[TemplateOptionEnum.FROM_NAME]) {
            clone.fromName = '';
        }

        if (!this.briefingSaveTemplateOption[TemplateOptionEnum.REPLY_TO]) {
            clone.replyTo = '';
        }

        if (!this.briefingSaveTemplateOption[TemplateOptionEnum.MAILING_LISTS]) {
            clone.mailingListIds = [];
        }

        if (!this.briefingSaveTemplateOption[TemplateOptionEnum.RECIPIENTS]) {
            clone.recipientList = [];
        }

        if (!this.briefingSaveTemplateOption[TemplateOptionEnum.SCHEDULING]) {
            clone.sendAt = new BriefingSendAtModel();
        }

        return clone;
    }


    private cleanUpBlock(block: BlockModel, copyStyles: boolean, index: string): boolean {
        switch (block.type.key) {
            case BriefingBlockTypeEnum.IMAGE:
                if (!copyStyles) {
                    block.displayElement.styles = new MonitoringImageStyleModel();
                }
                return this.briefingSaveTemplateOption[TemplateOptionEnum.IMAGE + index];
            case BriefingBlockTypeEnum.DATE:
                if (!copyStyles) {
                    block.displayElement.styles = new MonitoringDateStyleModel();
                }
                return this.briefingSaveTemplateOption[TemplateOptionEnum.DATE + index];
            case BriefingBlockTypeEnum.TITLE:
                if (!copyStyles) {
                    block.displayElement.styles = new MonitoringTextStyleModel();
                    (block.displayElement.styles as MonitoringTextStyleModel).color = '#5e6473';
                }
                return this.briefingSaveTemplateOption[TemplateOptionEnum.TITLE + index];
            case BriefingBlockTypeEnum.TEXT:
                if (!copyStyles) {
                    block.displayElement.styles = new MonitoringTextStyleModel();
                }
                return this.briefingSaveTemplateOption[TemplateOptionEnum.TEXT + index];
            case BriefingBlockTypeEnum.TABLE_OF_CONTENT:
                if (!copyStyles) {
                    block.displayElement.styles = new MonitoringTextStyleModel();
                    (block.displayElement.styles as MonitoringTextStyleModel).color = '#005494';
                }
                return this.briefingSaveTemplateOption[TemplateOptionEnum.TABLE_OF_CONTENTS + index];
            case BriefingBlockTypeEnum.CURATED_MENTION_LIST:
                const curatedDisplayElement = block.displayElement as CuratedMentionListDisplayElementModel;
                if (!copyStyles) {
                    curatedDisplayElement.styles = new MentionCustomizationModel();
                }

                // section rules
                if (!this.briefingSaveTemplateOption[TemplateOptionEnum.SECTION_RULES]) {
                    curatedDisplayElement.searchCriteria = new MentionSearchModel();
                }

                return this.briefingSaveTemplateOption[TemplateOptionEnum.CURATED_MENTION_LIST + index];
            case BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST:
                if (!copyStyles) {
                    block.displayElement.styles = new MentionCustomizationModel();
                }

                return this.briefingSaveTemplateOption[TemplateOptionEnum.AUTOMATIC_MENTION_LIST + index];
            case BriefingBlockTypeEnum.ANALYSIS:
                if (!copyStyles) {
                    block.displayElement.styles = new MonitoringTextStyleModel();
                }

                return this.briefingSaveTemplateOption[TemplateOptionEnum.ANALYSIS + index];
            case BriefingBlockTypeEnum.ONE_COLUMN_LAYOUT:
                if (!copyStyles) {
                    block.displayElement.styles = new MonitoringLayoutStyle();
                }
                return this.briefingSaveTemplateOption[TemplateOptionEnum.ONE_COLUMN_LAYOUT + index];
            case BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT:
                if (!copyStyles) {
                    block.displayElement.styles = new MonitoringLayoutStyle(null, LayoutBlockDisplayRatiosEnum.ONE_TO_ONE);
                }
                return this.briefingSaveTemplateOption[TemplateOptionEnum.TWO_COLUMN_LAYOUT + index];
            case BriefingBlockTypeEnum.THREE_COLUMN_LAYOUT:
                if (!copyStyles) {
                    block.displayElement.styles = new MonitoringLayoutStyle(null, LayoutBlockDisplayRatiosEnum.ONE_TO_ONE_TO_ONE);
                }
                return this.briefingSaveTemplateOption[TemplateOptionEnum.THREE_COLUMN_LAYOUT + index];

            case BriefingBlockTypeEnum.COVERAGE_CURATOR:
                if (!copyStyles) {
                    block.displayElement.styles = new CuratorStyle();
                }

                block.displayElement = block.displayElement.clone();

                return this.briefingSaveTemplateOption[TemplateOptionEnum.COVERAGE_CURATOR + index];
            default:
                // should not get here
                if (!copyStyles) {
                    block.displayElement.styles = new MentionCustomizationModel();
                }
                return true;
        }
    }


    /**
     * Helper function for converting template options to TreeNode
     */
    private templateOptionToTreeNode(options: Map<TemplateOptionEnum, TemplateOptionEnum[]>): TreeNode<string>[] {
        const treeNodes: TreeNode<string>[] = [];
        options.forEach((children, option) => {
            treeNodes.push({
                label: this.localizationService.transform(`Briefings.briefingSaveTemplateDialog.setTemplateContent.${option}`),
                data: option,
                children: children.map(subOption => {
                    return {
                        label: this.localizationService.transform(`Briefings.briefingSaveTemplateDialog.setTemplateContent.${subOption}`),
                        data: subOption,
                    };
                })
            });
        });


        treeNodes.forEach(item => {
            this.selectedOptions.push(item);
            item.children.forEach(sub => this.selectedOptions.push(sub));
        });

        return treeNodes;
    }

    /**
     * Helper function for converting content blocks to TreeNode
     */
    private contentBlocksToTreeNode(): void {
        if (!this.currentBriefing?.blocks?.length) {
            // if no block exists in briefing, we do not need content block option
            this.templateOptions = this.templateOptions.filter(option => option.data !== TemplateOptionEnum.CONTENT_BLOCK);
            return;
        }

        const contentBlockTreeNode = this.templateOptions.find(option => option.data === TemplateOptionEnum.CONTENT_BLOCK);

        this.currentBriefing.blocks.forEach((block, index) => {
            if (block.isLayoutBlockType()) {
                // As we could have multipe blocks of the same type, we use type+index as the key, e.g. TemplateOptionEnum.TITLE + index
                const templateOption = briefingBlockTypeToTemplateContentOptionMap.get(block.type.key as BriefingBlockTypeEnum);
                const layoutTree = {
                    label: this.localizationService.transform(`Briefings.briefingSaveTemplateDialog.setTemplateContent.${templateOption}`),
                    data: templateOption + index
                };
                (block as LayoutBlock).blocks.forEach((column, columnIndex) => {
                    column.forEach((x, rowIdex) => {
                        // As we could have multipe blocks of the same type, we use type+index as the key, e.g. TemplateOptionEnum.TITLE + index
                        const rowTemplateOption = briefingBlockTypeToTemplateContentOptionMap.get(x.type.key as BriefingBlockTypeEnum);

                        this.selectedOptions.push({
                            label: this.localizationService.transform(`Briefings.briefingSaveTemplateDialog.setTemplateContent.${rowTemplateOption}`),
                            data: rowTemplateOption + index + columnIndex + rowIdex
                        });
                    });
                });
                contentBlockTreeNode.children.push(layoutTree);
            } else {
                // As we could have multipe blocks of the same type, we use type+index as the key, e.g. TemplateOptionEnum.TITLE + index
                const templateOption = briefingBlockTypeToTemplateContentOptionMap.get(block.type.key as BriefingBlockTypeEnum);
                contentBlockTreeNode.children.push(
                    {
                        label: this.localizationService.transform(`Briefings.briefingSaveTemplateDialog.setTemplateContent.${templateOption}`),
                        data: templateOption + index
                    }
                );
            }
        });

        contentBlockTreeNode.children.forEach(item => this.selectedOptions.push(item));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
